import { useRef, useState } from 'react';
import { ImagePlus, X, Loader2, AlertCircle } from 'lucide-react';
import { uploadCargoPhoto } from '../../../shared/api/cloudinary';

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB, matches the backend's presigned-upload limit

type Props = {
  label: string;
  photoUrl: string | null;
  onChange: (url: string | null) => void;
  upload?: (file: File) => Promise<string>;
};

export function PhotoUpload({ label, photoUrl, onChange, upload = uploadCargoPhoto }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);

    if (!ALLOWED_TYPES.has(file.type)) {
      setError('Please choose a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('That file is too large, please choose one under 10 MB.');
      return;
    }

    setUploading(true);
    try {
      const url = await upload(file);
      onChange(url);
    } catch {
      setError("Couldn't upload that photo, please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
        {label} <span className="normal-case font-medium text-muted/70">(optional)</span>
      </label>

      {photoUrl ? (
        <div className="relative w-full max-w-[200px]">
          <img src={photoUrl} alt="Equipment" className="w-full aspect-square object-cover rounded-xl" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-surface border border-line flex items-center justify-center text-muted hover:text-text transition-colors"
            aria-label="Remove photo"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex flex-col items-center justify-center gap-2 w-full max-w-[200px] aspect-square rounded-xl bg-bg hover:bg-surface-raised transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 size={20} className="text-muted animate-spin" />
          ) : (
            <ImagePlus size={20} className="text-muted" />
          )}
          <span className="text-xs text-muted">{uploading ? 'Uploading…' : 'Add a photo'}</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {error && (
        <p className="flex items-center gap-1.5 text-xs text-danger mt-2">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
}
